import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetDomicileClassParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import { ProductPricing } from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { useSingleProduct } from 'Services/ProductManagement/Features/Product/Hooks'
import useSingleTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSingleTariff'
import { TariffProductTabType } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import useHash from 'Util/hooks/useHash'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Grid, Paper, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

type TabId = TariffProductTabType.DETAILS | TariffProductTabType.PRICING

function TariffProductDetailPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id, productId } = useParams()
  const { singleProduct, singleLoading: singleProductLoading } = useSingleProduct(productId)
  const previousLocation = usePreviousLocation(`${baseUrl}/${id}/products`)
  const { singleTariff } = useSingleTariff(id)
  const {
    data: productGroupParameterGroup = null,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined)
  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(undefined)
  const {
    data: allowedTokensParameterGroup = null,
    isLoading: isAllowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(undefined)
  const {
    data: domicileClassParameterGroup = null,
    isLoading: isDomicileClassParameterGroupLoading
  } = useGetDomicileClassParameterGroupQuery(undefined)
  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })
  const hash = useHash()
  const defaultTab = (hash as TabId) || TariffProductTabType.DETAILS
  const [tabValue, setTabValue] = useState(defaultTab)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabId) => {
    navigate(`#${newValue}`)
    setTabValue(newValue)
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : '',
      link: `${baseUrl}/${id}/`
    },
    {
      title: singleProduct ? getTranslation(singleProduct.translations, 'fi').name : ''
    }
  ]

  const isSingleProductLoading = singleProductLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Box sx={{ my: 2 }}>
            <TabContext value={tabValue}>
              <Paper>
                <TabList onChange={handleTabChange} aria-label={t`Tariff product tab list`}>
                  <Tab label={t`Product details`} value={TariffProductTabType.DETAILS} />
                  <Tab label={t`Pricing`} value={TariffProductTabType.PRICING} />
                </TabList>
              </Paper>
              <TabPanel sx={{ px: 0, pt: 2 }} value={TariffProductTabType.DETAILS}>
                <WithLoader loading={isSingleProductLoading}>
                  {singleProduct && (
                    <ProductDetails
                      product={singleProduct}
                      productGroupParameterGroup={productGroupParameterGroup}
                      isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
                      customerGroupParameterGroup={customerGroupParameterGroup}
                      isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
                      allowedTokensParameterGroup={allowedTokensParameterGroup}
                      isAllowedTokensParameterGroupLoading={isAllowedTokensParameterGroupLoading}
                      vatDefinition={vatDefinition}
                      isVatDefinitionsLoading={isVatDefinitionsLoading}
                    />
                  )}
                </WithLoader>
              </TabPanel>
              <TabPanel sx={{ p: 0 }} value={TariffProductTabType.PRICING}>
                <WithLoader
                  rows={ROWS_PER_PAGE_DEFAULT}
                  loading={isSingleProductLoading || isDomicileClassParameterGroupLoading}
                >
                  {singleProduct && (
                    <ProductPricing
                      product={singleProduct}
                      domicileClassParameterGroup={domicileClassParameterGroup}
                    />
                  )}
                </WithLoader>
              </TabPanel>
            </TabContext>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default TariffProductDetailPage
